/**
 * Noms des élèves : quel champ contient quoi, et classement alphabétique.
 *
 * ATTENTION — les deux colonnes portent l'inverse de ce qu'elles contiennent.
 * Le formulaire d'ajout d'élève lie le champ étiqueté « Nom de famille » à
 * `first_name`, et le champ « Prénom » à `last_name` (voir StudentForm.vue,
 * les deux premiers blocs du formulaire). Le nom de famille vit donc dans
 * `first_name`.
 *
 * Ce module est le seul endroit qui connaît cette inversion : les écrans
 * passent par `familyNameOf` / `givenNameOf` plutôt que de lire les champs,
 * et n'ont pas à se souvenir du piège. Le jour où les données seront remises
 * d'aplomb, les deux constantes ci-dessous suffiront.
 */

const FAMILY_NAME_FIELD = "first_name";
const GIVEN_NAME_FIELD = "last_name";

/** Nom de famille de l'élève, quel que soit le champ qui le porte. */
export function familyNameOf(student) {
  return String((student || {})[FAMILY_NAME_FIELD] || "").trim();
}

/** Prénom de l'élève. */
export function givenNameOf(student) {
  return String((student || {})[GIVEN_NAME_FIELD] || "").trim();
}

/**
 * Compare deux élèves par nom de famille, puis par prénom.
 *
 * `Intl.Collator` plutôt qu'une comparaison de chaînes : comparer les codes
 * de caractères placerait « Élodie » après « Zoé », et « ouattara » après
 * « Zamba » — l'ordre paraîtrait cassé sur la moitié des listes. La
 * sensibilité « base » range les accents à leur place et ne laisse ni la
 * casse ni les diacritiques départager deux noms autrement identiques.
 *
 * Un élève sans nom de famille passe en fin de liste : la chaîne vide le
 * remonterait en tête, où il n'a rien à faire.
 */
export function compareByFamilyName(left, right, collator) {
  const leftFamily = familyNameOf(left);
  const rightFamily = familyNameOf(right);

  if (!leftFamily !== !rightFamily) return leftFamily ? -1 : 1;

  return (
    collator.compare(leftFamily, rightFamily) ||
    collator.compare(givenNameOf(left), givenNameOf(right))
  );
}

/**
 * Élèves classés par nom de famille.
 *
 * Rend toujours un nouveau tableau : `sort` réordonne celui qu'on lui donne,
 * et la liste vient d'un fournisseur partagé entre écrans — la trier sur
 * place changerait l'ordre ailleurs dans l'application.
 *
 * @param {Array}  students
 * @param {string} locale  langue de l'interface, qui fixe les règles de tri
 */
export function sortByFamilyName(students, locale) {
  const collator = new Intl.Collator(locale || undefined, {
    sensitivity: "base",
    numeric: true,
  });
  return [...(students || [])].sort((left, right) =>
    compareByFamilyName(left, right, collator)
  );
}
